import React, { ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { I } from '../icons-ds';
import { Sidebar } from './Sidebar';
import { TabBar } from './TabBar';
import { useViewport } from './useViewport';
import type { Folder } from '../lib/types';

export type ShellProps = {
  children?: ReactNode;
  folders: Folder[];
  favoriteCount?: number;
  accent?: string;
};

/** Translate a sidebar/tabbar item id into a router path. */
export function targetToPath(target: string): string {
  if (target.startsWith('folder:')) {
    return '/folder/' + target.slice('folder:'.length);
  }
  switch (target) {
    case 'home':
      return '/search';
    case 'favorites':
      return '/favorites';
    case 'folders':
      return '/folders';
    case 'glossary':
      return '/glossary';
    case 'settings':
    case 'account':
      return '/settings';
    default:
      return '/search';
  }
}

/** Determine the active item id from the current location. */
function pathToActive(pathname: string): string {
  if (pathname.startsWith('/folder/')) {
    const segs = pathname.split('/').filter(Boolean); // ['folder', parent, child?]
    const parent = segs[1];
    const child = segs[2];
    return 'folder:' + parent + (child ? '/' + child : '');
  }
  if (pathname.startsWith('/favorites')) return 'favorites';
  if (pathname.startsWith('/folders')) return 'folders';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/glossary')) return 'glossary';
  return 'home';
}

const RAIL_ITEMS = [
  { id: 'home', icon: <I.search size={22} />, label: 'Search' },
  { id: 'favorites', icon: <I.starFill size={22} />, label: 'Favorites' },
  { id: 'folders', icon: <I.folder size={22} />, label: 'Folders' },
  { id: 'glossary', icon: <I.bookmark size={22} />, label: 'Glossary' },
  { id: 'settings', icon: <I.settings size={22} />, label: 'Settings' },
];

export function Shell({ children, folders, favoriteCount, accent }: ShellProps) {
  const viewport = useViewport();
  const navigate = useNavigate();
  const location = useLocation();

  const active = pathToActive(location.pathname);

  const onNavigate = useCallback(
    (target: string) => {
      navigate(targetToPath(target));
    },
    [navigate]
  );

  if (viewport === 'mobile') {
    // Let the document own vertical scrolling on iOS. A fixed full-screen
    // shell with a nested overflow scroller can jump as Safari's visual
    // viewport settles and can briefly swallow touch scrolling.
    return (
      <div className="dp-mobile-shell">
        <main className="dp-mobile-content">
          {children}
        </main>
        <TabBar active={active} onNavigate={onNavigate} />
      </div>
    );
  }

  if (viewport === 'tablet') {
    return (
      <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden', background: 'var(--bg-canvas)' }}>
        <nav
          style={{
            width: 88, flex: '0 0 auto', height: '100%', overflowY: 'auto',
            background: 'var(--bg-grouped)',
            borderRight: '1px solid var(--line-divider)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '16px 0', gap: 8,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <I.logo size={28} />
          </div>
          {RAIL_ITEMS.map((it) => (
            <button
              key={it.id}
              onClick={() => onNavigate(it.id)}
              title={it.label}
              aria-label={it.label}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                width: 64, flexShrink: 0, padding: '8px 0', borderRadius: 12,
                background: active === it.id ? 'var(--accent-fill)' : 'transparent',
                color: active === it.id ? 'var(--accent)' : 'var(--fg-secondary)',
                fontSize: 10, fontWeight: 500,
                border: 0, cursor: 'pointer',
                transition: 'all var(--dur-fast) var(--ease-out)',
              }}
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </nav>
        <main style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>{children}</main>
      </div>
    );
  }

  // desktop
  return (
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden', background: 'var(--bg-canvas)' }}>
      <Sidebar
        active={active}
        onNavigate={onNavigate}
        folders={folders}
        favoriteCount={favoriteCount}
        accent={accent}
      />
      <main style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>{children}</main>
    </div>
  );
}

export default Shell;
